/**
 * Fills a modal's body and footer with the outcome of copying a document (clone or duplicate).
 *
 * Built with DOM nodes rather than an HTML string, so document names land as text.
 *
 * @module DocumentCopyResultModal
 */

/**
 * A run of message text; `Strong` runs render bold.
 * @typedef {string | { Strong: string }} DocumentCopyResultTextRun
 */

/**
 * @typedef {object} DocumentCopyResultButton
 * @property {string} Label - The button text
 * @property {string} Icon - The Font Awesome icon class, e.g. `fa-times`
 * @property {string} ClassName - The Bulma button classes, e.g. `button is-link is-soft`
 * @property {string} Title - The button's tooltip
 * @property {(pEvent: MouseEvent) => void} onClick - Runs when the button is clicked
 */

/**
 * @typedef {object} DocumentCopyResult
 * @property {boolean} Succeeded - Picks the success or failure icon colour
 * @property {string} Title - The headline
 * @property {Array<DocumentCopyResultTextRun>} Message - The explanation under the headline
 * @property {string} Instruction - What the user should do next
 * @property {Array<DocumentCopyResultButton>} Buttons - The footer buttons, left to right
 */

/**
 * @param {string} pIconClassName - The full class list for the `<i>` element
 * @param {boolean} [pHidden] - Whether to hide the icon from assistive technology
 * @return {HTMLSpanElement}
 */
function createIcon(pIconClassName, pHidden)
{
	const tmpIconWrapper = document.createElement('span');
	tmpIconWrapper.className = 'icon';
	const tmpIcon = document.createElement('i');
	tmpIcon.className = pIconClassName;
	if (pHidden)
	{
		tmpIcon.setAttribute('aria-hidden', 'true');
	}
	tmpIconWrapper.appendChild(tmpIcon);
	return tmpIconWrapper;
}

/**
 * @param {string} pClassName
 * @param {Array<DocumentCopyResultTextRun>} pRuns
 * @return {HTMLParagraphElement}
 */
function createParagraph(pClassName, pRuns)
{
	const tmpParagraph = document.createElement('p');
	tmpParagraph.className = pClassName;
	for (const tmpRun of pRuns)
	{
		if (typeof tmpRun === 'string')
		{
			tmpParagraph.append(tmpRun);
		}
		else
		{
			const tmpStrong = document.createElement('strong');
			tmpStrong.textContent = (tmpRun.Strong === undefined || tmpRun.Strong === null) ? '' : String(tmpRun.Strong);
			tmpParagraph.appendChild(tmpStrong);
		}
	}
	return tmpParagraph;
}

/**
 * @param {DocumentCopyResultButton} pButton
 * @return {HTMLButtonElement}
 */
function createButton(pButton)
{
	const tmpButton = document.createElement('button');
	tmpButton.className = pButton.ClassName;
	tmpButton.type = 'button';
	tmpButton.title = pButton.Title;
	tmpButton.appendChild(createIcon(`fas fa-fw ${pButton.Icon}`, true));
	const tmpLabel = document.createElement('span');
	tmpLabel.textContent = pButton.Label;
	tmpButton.appendChild(tmpLabel);
	tmpButton.addEventListener('click', pButton.onClick);
	return tmpButton;
}

/**
 * Replace the contents of a modal's body and footer with a copy result.
 *
 * @param {HTMLElement} pBodyElement - The modal body container
 * @param {HTMLElement} pFooterElement - The modal footer container
 * @param {DocumentCopyResult} pResult - What to show
 * @return {void}
 */
function renderDocumentCopyResult(pBodyElement, pFooterElement, pResult)
{
	const tmpContainer = document.createElement('div');
	tmpContainer.className = 'container column is-12';

	const tmpHeadline = document.createElement('p');
	tmpHeadline.className = 'title is-5 has-text-centered p-2 mb-5';
	tmpHeadline.appendChild(createIcon(`fa fa-fw fa-circle-check ${pResult.Succeeded ? 'has-text-success' : 'has-text-danger'}`));
	const tmpHeadlineText = document.createElement('span');
	tmpHeadlineText.textContent = ` ${pResult.Title}`;
	tmpHeadline.appendChild(tmpHeadlineText);
	tmpContainer.appendChild(tmpHeadline);

	tmpContainer.appendChild(createParagraph('subtitle is-6 has-text-centered', pResult.Message));
	tmpContainer.appendChild(createParagraph('subtitle is-6 has-text-centered', [ pResult.Instruction ]));

	pBodyElement.innerHTML = '';
	pBodyElement.appendChild(tmpContainer);
	pFooterElement.innerHTML = '';
	for (const tmpButton of pResult.Buttons)
	{
		pFooterElement.appendChild(createButton(tmpButton));
	}
}

/**
 * What a successful copy tells the user to do next, and the footer buttons that go with it.
 *
 * With an opener: Close, then Open New Document. Without one (the page cannot open another document), the
 * instruction points to the forms list and the footer is a single Done.
 *
 * @param {() => void} fDismiss - Closes the modal
 * @param {?(pEvent: MouseEvent) => void} fOpenNewDocument - Opens the new document; runs before the modal closes
 * @return {{ Instruction: string, Buttons: Array<DocumentCopyResultButton> }}
 */
function createNewDocumentFollowUp(fDismiss, fOpenNewDocument)
{
	if (!fOpenNewDocument)
	{
		return {
			Instruction: 'To open it, go back to your forms list and sync. The new document will be listed there.',
			Buttons: [ { Label: 'Done', Icon: 'fa-check', ClassName: 'button is-link', Title: 'Close modal', onClick: () => fDismiss() } ],
		};
	}
	return {
		Instruction: 'Please click button below to open your new document or close this modal.',
		Buttons: [
			{ Label: 'Close', Icon: 'fa-times', ClassName: 'button is-link is-soft', Title: 'Close modal', onClick: () => fDismiss() },
			{
				Label: 'Open New Document',
				Icon: 'fa-external-link-alt',
				ClassName: 'button is-link',
				Title: 'Open new document',
				onClick: (pEvent) =>
				{
					fOpenNewDocument(pEvent);
					fDismiss();
				},
			},
		],
	};
}

module.exports = { renderDocumentCopyResult, createNewDocumentFollowUp };
